import { useEffect, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { io } from 'socket.io-client';
import { authAtom } from '../../store/auth';
import { NOTIFICATION_AUDIENCE } from '../../api/notificationsApi';
import {
  notificationKeys,
  useNotificationActions,
} from '../../Hooks/useNotifications';
import {
  isPushSupported,
  registerNotificationWorker,
  syncPushSubscription,
} from '../../utils/pushNotifications';
import { showNotificationToast } from './NotificationToast';

const NotificationsBridge = () => {
  const token = useAtomValue(authAtom)?.access_token;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { markRead } = useNotificationActions();

  const openRef = useRef(null);
  openRef.current = (notification) => {
    if (notification?.id && !notification.read) markRead.mutate(notification.id);
    navigate(notification?.link || '/notifications');
  };

  const markReadRef = useRef(null);
  markReadRef.current = (id) => markRead.mutate(id);

  useEffect(() => {
    if (!token || !process.env.REACT_APP_API_BASE_URL) return undefined;

    const refresh = () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unread() });
    };

    const setUnread = (count) => {
      if (typeof count === 'number') {
        queryClient.setQueryData(notificationKeys.unread(), count);
      }
    };

    const socket = io(process.env.REACT_APP_API_BASE_URL, {
      auth: (callback) =>
        callback({
          token: Cookies.get('token') || token,
          audience: NOTIFICATION_AUDIENCE,
        }),
      transports: ['websocket', 'polling'],
      reconnectionDelayMax: 30_000,
    });

    socket.on('connect', refresh);
    socket.on('notification:new', ({ notification, unread_count: unread } = {}) => {
      setUnread(unread);
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
      if (notification && document.visibilityState === 'visible') {
        showNotificationToast(notification, (n) => openRef.current(n));
      }
    });
    socket.on('notification:sync', ({ unread_count: unread } = {}) => {
      setUnread(unread);
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      queryClient.removeQueries({ queryKey: notificationKeys.all });
    };
  }, [token, queryClient]);

  useEffect(() => {
    if (!token || !isPushSupported()) return undefined;

    registerNotificationWorker()
      .then(() => syncPushSubscription())
      .catch(() => null);

    const onMessage = (event) => {
      const message = event.data || {};
      if (message.type === 'notification:push') {
        queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
        queryClient.invalidateQueries({ queryKey: notificationKeys.unread() });
      } else if (message.type === 'notification:open') {
        openRef.current({ id: message.id, link: message.link, read: false });
      } else if (message.type === 'notification:resubscribe') {
        syncPushSubscription().catch(() => null);
      }
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [token, queryClient]);

  useEffect(() => {
    if (!token) return;
    const params = new URLSearchParams(location.search);
    const id = params.get('notification');
    if (!id) return;
    markReadRef.current(id);
    params.delete('notification');
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true }
    );
  }, [token, location.pathname, location.search, navigate]);

  return null;
};

export default NotificationsBridge;
